export enum ReverseVestingScheduleIssue {
  CLIFF_EXCEEDS_DURATION = 'cliff-exceeds-duration',
  CLIFF_NOT_DIVISIBLE_BY_FREQUENCY = 'cliff-not-divisible-by-frequency',
  DURATION_NOT_DIVISIBLE_BY_FREQUENCY = 'duration-not-divisible-by-frequency',
  FREQUENCY_EXCEEDS_DURATION = 'frequency-exceeds-duration',
}

export type ReverseVestingScheduleField = 'cliffMonths' | 'durationMonths' | 'frequencyMonths';

export interface ReverseVestingScheduleViolation {
  field: ReverseVestingScheduleField;
  issue: ReverseVestingScheduleIssue;
}

export interface ReverseVestingScheduleInput {
  cliffMonths?: null | number;
  durationMonths?: null | number;
  frequencyMonths?: null | number;
}

/**
 * The four schedule rules reverse vesting shares with regular plan vesting. Kept here, in a
 * dependency-free function, so the backend Zod schema and the frontend Zod schema apply exactly
 * the same rules — the plan-vesting equivalents drifted until the backend's cliff-divisibility
 * validator became unreachable, and this exists so that cannot happen again.
 *
 * Returns every violation rather than the first, so a form can highlight all offending fields at
 * once. Incomplete input yields no violations: requiredness belongs to the caller's schema.
 */
export const validateReverseVestingSchedule = ({
  cliffMonths,
  durationMonths,
  frequencyMonths,
}: ReverseVestingScheduleInput): ReverseVestingScheduleViolation[] => {
  const violations: ReverseVestingScheduleViolation[] = [];

  const hasDuration = typeof durationMonths === 'number' && durationMonths > 0;
  const hasFrequency = typeof frequencyMonths === 'number' && frequencyMonths > 0;
  const hasCliff = typeof cliffMonths === 'number' && cliffMonths > 0;

  if (hasDuration && hasFrequency) {
    if (frequencyMonths > durationMonths) {
      violations.push({
        field: 'frequencyMonths',
        issue: ReverseVestingScheduleIssue.FREQUENCY_EXCEEDS_DURATION,
      });
    } else if (durationMonths % frequencyMonths !== 0) {
      violations.push({
        field: 'durationMonths',
        issue: ReverseVestingScheduleIssue.DURATION_NOT_DIVISIBLE_BY_FREQUENCY,
      });
    }
  }

  if (hasCliff && hasDuration && cliffMonths > durationMonths) {
    violations.push({
      field: 'cliffMonths',
      issue: ReverseVestingScheduleIssue.CLIFF_EXCEEDS_DURATION,
    });
  }

  if (hasCliff && hasFrequency && cliffMonths % frequencyMonths !== 0) {
    violations.push({
      field: 'cliffMonths',
      issue: ReverseVestingScheduleIssue.CLIFF_NOT_DIVISIBLE_BY_FREQUENCY,
    });
  }

  return violations;
};
